import type { MockedResponse } from "@apollo/client/testing";
import { COMMISSION_RELEASE_BATCHES_QUERY } from "../release/queries";
import type { CommissionReleaseBatchNode } from "../release/types";
import { commissionProgramFixtures } from "./fixtures";
import { withLineTypenames } from "./linesMocks";
import { commissionReleaseBatchFixtures } from "./releaseFixtures";
import { buildConnection } from "./connection";
import { pushActivityEvent } from "./historyMocks";
import { createCatalogMock, withTypenames, type MockResolver } from "./mockUtils";
import { runtimeCommissionLines, runtimeCounters, runtimePayoutRuns, runtimeReleaseBatches } from "./runtimeState";

function withReleaseBatchTypenames(batch: CommissionReleaseBatchNode) {
  return {
    __typename: "BackofficeCommissionReleaseBatch",
    ...batch,
    program: withTypenames(batch.program, "BackofficeCommissionProgramSummary")
  };
}

function buildReleaseBatchesMockData() {
  const nodes = [...commissionReleaseBatchFixtures, ...Object.values(runtimeReleaseBatches)].map(
    withReleaseBatchTypenames
  );

  return {
    commissionReleaseBatches: buildConnection("BackofficeCommissionReleaseBatchConnection", nodes)
  };
}

function buildReleaseBatchMockData(batchId: string) {
  const fixture = commissionReleaseBatchFixtures.find((batch) => batch.id === batchId);
  const runtime = runtimeReleaseBatches[batchId];
  const batch = fixture ?? runtime;
  if (!batch) {
    return { commissionReleaseBatch: null };
  }

  const payoutRuns = (runtimePayoutRuns[batchId] ?? []).map((run) => ({
    __typename: "BackofficeCommissionPayoutRun",
    ...run,
    person: withTypenames(run.person, "BackofficeCommissionEnrollmentPerson"),
    lineItems: run.lineItems.map((item) => ({
      __typename: "BackofficeCommissionPayoutLineItem",
      ...item,
      line: withTypenames(item.line, "BackofficeCommissionLine")
    }))
  }));

  const stagedLines = batch.requestedLineIds
    .map((lineId) => runtimeCommissionLines.find((line) => line.id === lineId))
    .filter((line) => line != null)
    .map(withLineTypenames);

  return {
    commissionReleaseBatch: {
      ...withReleaseBatchTypenames(batch),
      lines: buildConnection("BackofficeCommissionLineConnection", stagedLines),
      payoutRuns
    }
  };
}

export const commissionReleaseBatchesApolloMock = createCatalogMock(
  COMMISSION_RELEASE_BATCHES_QUERY,
  buildReleaseBatchesMockData()
);

export const releaseApolloMocks: MockedResponse[] = [commissionReleaseBatchesApolloMock];

export const releaseMockResults: Record<string, Record<string, unknown>> = {
  CommissionReleaseBatches: buildReleaseBatchesMockData()
};

export const releaseVariableMockResolvers: Record<string, MockResolver> = {
  CommissionReleaseBatches: () => buildReleaseBatchesMockData(),
  CommissionReleaseBatch: (variables) => buildReleaseBatchMockData(String(variables.id)),
  StageCommissionRelease: (variables) => {
    runtimeCounters.releaseBatch += 1;
    const programId = String(variables.programId);
    const requestedLineIds = ((variables.lineIds as string[]) ?? []).map(String);
    const stagedLines = runtimeCommissionLines.filter(
      (line) =>
        requestedLineIds.includes(line.id) &&
        line.fundingStatus === "FUNDED" &&
        line.payoutEligibilityStatus === "PAYABLE" &&
        line.campaign.program.id === programId
    );

    if (stagedLines.length === 0) {
      return {
        commissionStageRelease: {
          success: false,
          message: "No payable funded lines to stage.",
          releaseBatch: null
        }
      };
    }

    const totalAmountCents = stagedLines.reduce((sum, line) => sum + line.amountCents, 0);
    const program = commissionProgramFixtures.find((node) => node.id === programId);

    const batch: CommissionReleaseBatchNode = {
      id: `commission-release-batch-runtime-${runtimeCounters.releaseBatch}`,
      status: "STAGED",
      batchType: (variables.batchType as CommissionReleaseBatchNode["batchType"]) ?? "AD_HOC",
      totalAmountCents,
      selectionCount: stagedLines.length,
      lineCount: stagedLines.length,
      requestedLineIds,
      exceptionCount: requestedLineIds.length - stagedLines.length,
      depositFileKey: null,
      payoutRunCount: stagedLines.length,
      exportedAt: null,
      createdAt: new Date().toISOString(),
      program: {
        id: program?.id ?? programId,
        name: program?.name ?? "Golf Retirement Plus",
        slug: program?.slug ?? "grp"
      }
    };

    runtimeReleaseBatches[batch.id] = batch;
    runtimePayoutRuns[batch.id] = stagedLines.map((line, index) => ({
      id: `commission-payout-run-runtime-${runtimeCounters.releaseBatch}-${index + 1}`,
      status: "PENDING",
      pgaId: line.person.pgaId ?? "",
      personDisplayName: line.person.displayName,
      person: { id: line.person.id, displayName: line.person.displayName, pgaId: line.person.pgaId },
      periodSummary: { releaseBatchId: batch.id, cutoff: batch.createdAt, lineCount: 1 },
      lineItems: [
        {
          id: `commission-payout-line-item-runtime-${runtimeCounters.releaseBatch}-${index + 1}`,
          amountCents: line.amountCents,
          line: { id: line.id, amountCents: line.amountCents }
        }
      ],
      amountCents: line.amountCents,
      lineCount: 1
    }));

    stagedLines.forEach((line) => {
      const lineIndex = runtimeCommissionLines.findIndex((node) => node.id === line.id);
      if (lineIndex >= 0) {
        runtimeCommissionLines[lineIndex] = {
          ...line,
          releaseBatch: { id: batch.id, status: "STAGED" }
        };
      }
    });

    pushActivityEvent("RELEASE_BATCH", batch.id, "STAGED", `Release staged — ${stagedLines.length} lines.`);

    return {
      commissionStageRelease: {
        success: true,
        message: "Release batch staged.",
        releaseBatch: withReleaseBatchTypenames(batch)
      }
    };
  },
  ExecuteCommissionRelease: (variables) => {
    const batchId = String(variables.batchId);
    const batch = runtimeReleaseBatches[batchId] ?? commissionReleaseBatchFixtures.find((node) => node.id === batchId);
    if (!batch) {
      return {
        commissionExecuteRelease: {
          success: false,
          message: "Release batch was not found.",
          releaseBatch: null
        }
      };
    }

    if (batch.status !== "STAGED") {
      return {
        commissionExecuteRelease: {
          success: false,
          message: "Only staged release batches can be executed.",
          releaseBatch: withReleaseBatchTypenames(batch)
        }
      };
    }

    const executed: CommissionReleaseBatchNode = {
      ...batch,
      status: "EXPORTED",
      depositFileKey: `corebridge/deposits/runtime-${batchId}.csv`,
      exportedAt: new Date().toISOString()
    };
    runtimeReleaseBatches[batchId] = executed;
    runtimePayoutRuns[batchId] = (runtimePayoutRuns[batchId] ?? []).map((run) => ({
      ...run,
      status: "EXPORTED"
    }));

    const executedAt = new Date().toISOString();
    runtimeCommissionLines.forEach((line, index) => {
      if (line.releaseBatch?.id === batchId) {
        runtimeCommissionLines[index] = {
          ...line,
          payoutEligibilityStatus: "PAID_OUT",
          paidOutAt: executedAt,
          releaseBatch: { id: batchId, status: "EXPORTED" }
        };
      }
    });

    pushActivityEvent("RELEASE_BATCH", batchId, "EXPORTED", "Release exported — payout runs posted.");

    return {
      commissionExecuteRelease: {
        success: true,
        message: "Release batch executed.",
        releaseBatch: withReleaseBatchTypenames(executed)
      }
    };
  }
};
